import { Suspense, lazy, type ComponentType, type ReactElement } from 'react';
import type { RouteObject } from 'react-router-dom';
import { RequirePermission } from '@web/auth/guards';
import type { PermissionRequirement } from '@web/auth/permissions';
import { PageLoader } from '@web/ui/PageLoader';
import type { RouteHandle } from './types';

type Loader = () => Promise<{ default: ComponentType }>;

/**
 * Lazy page element (own JS chunk) with an optional permission guard.
 * Call it at module-definition time (not inside a component).
 */
export function page(loader: Loader, permission?: PermissionRequirement): ReactElement {
  const Component = lazy(loader);
  const el = (
    <Suspense fallback={<PageLoader />}>
      <Component />
    </Suspense>
  );
  return permission ? <RequirePermission perm={permission}>{el}</RequirePermission> : el;
}

export interface LazyRouteOptions {
  permission?: PermissionRequirement;
  /** i18n key of the breadcrumb */
  crumb?: string;
}

/**
 * @example lazyRoute('iam/roles', () => import('./pages/RolesPage'), { permission: 'iam.role.read', crumb: 'iam:nav.roles' })
 */
export function lazyRoute(path: string, loader: Loader, opts: LazyRouteOptions = {}): RouteObject {
  const handle: RouteHandle | undefined = opts.crumb ? { crumb: opts.crumb } : undefined;
  return { path, element: page(loader, opts.permission), handle };
}

/** Index child of a route group (the list page of `iam/users`). */
export function indexRoute(loader: Loader, permission?: PermissionRequirement): RouteObject {
  return { index: true, element: page(loader, permission) };
}

/**
 * Parent route that only contributes a breadcrumb; children render in its place.
 * @example routeGroup('iam/users', 'iam:nav.users', [indexRoute(list, perm), lazyRoute(':id', detail, { crumb: 'iam:users.detail' })])
 */
export function routeGroup(path: string, crumb: string, children: RouteObject[]): RouteObject {
  return { path, handle: { crumb } satisfies RouteHandle, children };
}
